import React, { useCallback } from 'react';
import { FormProvider, useFieldArray, UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { computeText } from '@/locales/utils';
import { Text } from '@/components/atoms/Text';
import { Input } from '@/components/form/Input';
import { getUseCustomerFormAutoGenerateEmailAndPassword } from '@/redux/launchDarkly';
import { AddBadgeButton, FlexContainer, GeneralInformationContainer } from './CreateCustomerForm.style';
import { isValidPassword } from './CreateCustomerForm.utils';
import { BaseCustomerAttributes } from '../CreateCustomerModal.types';
import { isValidPhoneNumber } from 'libphonenumber-js';
import BadgeInput from '@/components/form/BadgeInput';
import { generateBadgeNumber } from '@/utils/factories/customer/customer.utils';
import { SVGIcons } from '@/components/atoms/Icon/SVGIcon.interface';
import { SVGIcon } from '@/components/atoms/Icon/SVGIcon';
import { Badge } from '@/services/innovorder/customer/customer.types';
import PricingConfigurationSection from './PricingConfigurationSection';

type CreateCustomerFormProps = {
    customerForm: UseFormReturn<BaseCustomerAttributes>;
    brandId: number;
};

const CreateCustomerForm: React.FunctionComponent<React.PropsWithChildren<CreateCustomerFormProps>> = ({
    customerForm,
    brandId,
}) => {
    const intl = useIntl();

    const customerFormAutoGenerateEmailAndPassword = useSelector(getUseCustomerFormAutoGenerateEmailAndPassword);
    const { fields, append, remove, update } = useFieldArray<BaseCustomerAttributes, 'badges'>({
        control: customerForm.control,
        name: 'badges',
        rules: {
            minLength: {
                value: 1,
                message: computeText(intl, 'field.required'),
            },
        },
    });

    const addBadge = useCallback(() => {
        const badgeNumber = generateBadgeNumber();
        append({ value: badgeNumber, type: '', isDefault: false });
    }, [append]);

    const deleteBadge = useCallback(
        (index: number) => {
            if (fields[index].isDefault) {
                update(0, { ...fields[0], isDefault: true });
            }
            remove(index);
        },
        [remove, update, fields],
    );
    const starBadge = useCallback(
        (index: number) => {
            const currentSelectedBadgeIndex = fields.findIndex((field) => field.isDefault);
            update(currentSelectedBadgeIndex, { ...fields[currentSelectedBadgeIndex], isDefault: false });
            update(index, { ...fields[index], isDefault: true });
        },
        [update, fields],
    );

    const { watch } = customerForm;

    return (
        <FormProvider {...customerForm}>
            <form>
                <FlexContainer>
                    <Text text={'customer.form.clientInfoSection'} weight="bold" />
                    <GeneralInformationContainer>
                        <Input
                            placeholder={`customer.form.firstName`}
                            labelId={`customer.form.firstName`}
                            name="firstName"
                            rules={{
                                required: computeText(intl, 'field.required'),
                            }}
                        />
                        <Input
                            placeholder={`customer.form.lastName`}
                            labelId={`customer.form.lastName`}
                            name="lastName"
                            rules={{
                                required: computeText(intl, 'field.required'),
                            }}
                        />
                        <Input
                            placeholder={`customer.form.email`}
                            labelId={`customer.form.email`}
                            name="email"
                            rules={{
                                required: computeText(intl, 'field.required'),
                                pattern: {
                                    value: /^[A-Z\d._%+-]+@[A-Z\d.-]+\.[A-Z]{2,5}$/i,
                                    message: computeText(intl, 'field.invalidEmail'),
                                },
                            }}
                            isTouched={customerFormAutoGenerateEmailAndPassword}
                        />
                        <Input
                            placeholder={`customer.form.phoneNumber`}
                            labelId={`customer.form.phoneNumber`}
                            name="phoneNumber"
                            rules={{
                                validate: (phoneNumber: string) => {
                                    if (phoneNumber && !isValidPhoneNumber(phoneNumber, 'FR')) {
                                        return computeText(intl, 'field.invalidPhoneNumber');
                                    }
                                    return true;
                                },
                            }}
                        />
                        <Input
                            placeholder={`customer.form.password`}
                            labelId={`customer.form.password`}
                            name="password"
                            type="password"
                            rules={{
                                required: computeText(intl, 'field.required'),
                                validate: (password: string) => {
                                    if (!isValidPassword(password)) {
                                        return computeText(intl, 'field.invalidPassword');
                                    }
                                    return true;
                                },
                            }}
                            isTouched={customerFormAutoGenerateEmailAndPassword}
                        />
                        <Input
                            placeholder={`customer.form.confirmPassword`}
                            labelId={`customer.form.confirmPassword`}
                            name="confirmPassword"
                            type="password"
                            rules={{
                                required: computeText(intl, 'field.required'),
                                validate: (confirmPassword: string) => {
                                    if (watch('password') !== confirmPassword) {
                                        return computeText(intl, 'field.unmatchedPassword');
                                    }
                                    return true;
                                },
                            }}
                            isTouched={customerFormAutoGenerateEmailAndPassword}
                        />
                        <div style={{ gridColumn: 'span 2' }}>
                            <Input
                                placeholder={`customer.form.studentNumber`}
                                labelId={`customer.form.studentNumber`}
                                name="studentNumber"
                            />
                        </div>
                        <Input
                            placeholder={`customer.form.arrivalDate`}
                            labelId={`customer.form.arrivalDate`}
                            name="arrivalDate"
                            type="date"
                        />
                        <Input
                            placeholder={`customer.form.departureDate`}
                            labelId={`customer.form.departureDate`}
                            name="departureDate"
                            type="date"
                        />
                        {fields.map((field, index) => (
                            <BadgeInput
                                starBadge={starBadge}
                                deleteBadge={deleteBadge}
                                key={field.id}
                                badge={{ ...field, index } as unknown as Badge & { index: number }}
                            />
                        ))}

                        <AddBadgeButton type="button" onClick={addBadge}>
                            <SVGIcon icon={SVGIcons.PLUS} size={18} />
                            <Text text={'customer.form.addBadge'} weight="bold" />
                        </AddBadgeButton>
                    </GeneralInformationContainer>
                    <PricingConfigurationSection brandId={brandId} />
                </FlexContainer>
            </form>
        </FormProvider>
    );
};

export default CreateCustomerForm;
